export const DEFAULT_AUTO_CLASS_CHANGE_OFFSET_MINUTES = 5;

const resolveOffsetMinutesBeforeStart = (value = {}) => {
  const rawMinutes = value?.offsetMinutesBeforeStart;
  if (rawMinutes === undefined || rawMinutes === null || rawMinutes === "") {
    return DEFAULT_AUTO_CLASS_CHANGE_OFFSET_MINUTES;
  }

  return Math.max(0, Number(rawMinutes) || 0);
};

export const normalizeAutoClassChange = (value = {}) => ({
  active: value?.active !== false,
  offsetMinutesBeforeStart: resolveOffsetMinutesBeforeStart(value),
  offsetSecondsBeforeStart: Math.max(0, Math.min(59, Number(value?.offsetSecondsBeforeStart) || 0)),
});

export const getAutoClassChangeOffsetSeconds = (autoClassChange = {}) => {
  const normalized = normalizeAutoClassChange(autoClassChange);
  return (normalized.offsetMinutesBeforeStart * 60) + normalized.offsetSecondsBeforeStart;
};

export const isAutoClassChangeQuickOptionSelected = (autoClassChange, offsetMinutes) => {
  const normalized = normalizeAutoClassChange(autoClassChange);
  return normalized.offsetMinutesBeforeStart === offsetMinutes && normalized.offsetSecondsBeforeStart === 0;
};

export const getAutoClassChangeQuickOptionLabel = (offsetMinutes) => {
  if (offsetMinutes === 0) {
    return "At Class Start Time";
  }
  return `${offsetMinutes} min before Class Start Time`;
};

export const areAutoClassChangeSettingsEqual = (left, right) => {
  const normalizedLeft = normalizeAutoClassChange(left);
  const normalizedRight = normalizeAutoClassChange(right);
  return (
    normalizedLeft.active === normalizedRight.active
    && normalizedLeft.offsetMinutesBeforeStart === normalizedRight.offsetMinutesBeforeStart
    && normalizedLeft.offsetSecondsBeforeStart === normalizedRight.offsetSecondsBeforeStart
  );
};

export const getOtherClassIdsForAutoClassChangeApply = (classes = [], currentClassId = "") => (
  Array.from(new Set(
    (Array.isArray(classes) ? classes : [])
      .map((classItem) => classItem?.classId)
      .filter((classId) => Boolean(classId) && classId !== currentClassId)
  ))
);

export const shouldPromptAutoClassChangeApplyScope = ({
  previousAutoClassChange,
  nextAutoClassChange,
  otherClassIds = [],
} = {}) => (
  otherClassIds.length > 0
  && !areAutoClassChangeSettingsEqual(previousAutoClassChange, nextAutoClassChange)
);

export const resolveAutoClassChangeApplyTargetIds = ({
  scope,
  otherClassIds = [],
  selectedClassIds = [],
} = {}) => {
  if (scope === "all") {
    return [...otherClassIds];
  }
  if (scope === "selected") {
    const allowedIds = new Set(otherClassIds);
    return Array.from(new Set(
      (Array.isArray(selectedClassIds) ? selectedClassIds : [])
        .filter((classId) => allowedIds.has(classId))
    ));
  }
  return [];
};

export const formatAutoClassChangeOffsetSummary = (autoClassChange = {}) => {
  const totalSeconds = getAutoClassChangeOffsetSeconds(autoClassChange);
  if (totalSeconds === 0) {
    return "Transitions at class start time. Configured in Add/Edit Class.";
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (seconds === 0) {
    return `Transitions ${minutes} min before class start time. Configured in Add/Edit Class.`;
  }

  if (minutes === 0) {
    return `Transitions ${seconds} sec before class start time. Configured in Add/Edit Class.`;
  }

  return `Transitions ${minutes} min ${seconds} sec before class start time. Configured in Add/Edit Class.`;
};
